import { useFilterParam } from "@freightsimple/ui-core/Hooks/useFilterParam";
import {
  SavedCommodityCreatedSourceEnum,
  type SavedCommodity,
} from "@freightsimple/generated-dashboard-openapi-client/models/SavedCommodity";
import {
  selectSavedCommoditiesByProblem,
  problemCounts,
  type SavedCommodityProblem,
} from "../Helpers/savedCommoditiesProblems";

/** The empty selection, as it appears in a dropdown and in the URL. */
const ANY = "any";

/**
 * The Saved Commodities screen's two filters, held in the URL.
 *
 * In the URL rather than component state, so a filtered view is a link — that's what lets the Shopify
 * app send a merchant straight to "the ones your sync couldn't quote" (ENG-6665). Back/forward and a
 * reload also keep the view, which matters when the work is 63 products one at a time.
 */
export function useSavedCommoditiesFilters(
  savedCommodities: SavedCommodity[] | undefined,
) {
  const [source, setSource] = useFilterParam<string>("source", ANY);
  const [problem, setProblem] = useFilterParam<string>("problem", ANY);

  const all = savedCommodities ?? [];
  const selectedSource = isKnownSource(source) ? source : undefined;
  const selectedProblem = isKnownProblem(problem) ? problem : undefined;

  return {
    source,
    setSource,
    problem,
    setProblem,
    filtered: selectSavedCommoditiesByProblem(all, {
      source: selectedSource,
      problem: selectedProblem,
    }),
    counts: problemCounts(all, selectedSource),
    /** So the screen can say "3 of 3,473" rather than only the filtered number. */
    total: all.length,
    filtering: selectedSource !== undefined || selectedProblem !== undefined,
  };
}

/**
 * An unrecognised param means no filter, not an empty result: showing nothing would read as "you have
 * no products with this problem" — wrong, and the answer a merchant would act on.
 */
function isKnownSource(
  value: string,
): value is SavedCommodityCreatedSourceEnum {
  return Object.values<string>(SavedCommodityCreatedSourceEnum).includes(value);
}

function isKnownProblem(value: string): value is SavedCommodityProblem {
  return value === "missingDimensions" || value === "missingSku";
}

export const SAVED_COMMODITY_FILTER_ANY = ANY;
